import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  InputAdornment,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useStripeApi } from '../../hooks/useStripeApi';
import {
  formatChargeDisplay,
  formatCustomerDisplay,
  stripeDashboardBase,
  type StripeCharge,
} from '../../services/stripeApi';
import { listStripeChargesResolved } from '../../services/stripeResolved';
import { fmtStripeMoney, fmtStripeUnix, StripeHero, StripeStatCard, StripeStatusChip } from '../../components/stripe/stripeShared';

const StripeTransactionsPage: React.FC = () => {
  const { settings, isSecretConfigured, localSecretKey } = useStripeApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashBase = localSecretKey ? stripeDashboardBase(localSecretKey) : 'https://dashboard.stripe.com';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [charges, setCharges] = useState<StripeCharge[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();

  const [from, setFrom] = useState(searchParams.get('from') || format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [to, setTo] = useState(searchParams.get('to') || format(new Date(), 'yyyy-MM-dd'));
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const createdRange = useMemo(() => {
    const gte = Math.floor(new Date(from).getTime() / 1000);
    const toEnd = new Date(to);
    toEnd.setHours(23, 59, 59, 999);
    const lte = Math.floor(toEnd.getTime() / 1000);
    return { gte, lte };
  }, [from, to]);

  const load = useCallback(
    async (append = false, after?: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await listStripeChargesResolved(settings, isSecretConfigured, {
          limit: 50,
          starting_after: after,
          createdGte: createdRange.gte,
          createdLte: createdRange.lte,
        });
        setCharges((prev) => (append ? [...prev, ...res.data] : res.data));
        setHasMore(res.has_more);
        const last = res.data[res.data.length - 1];
        setCursor(last?.id);
      } catch (e) {
        const msg = axios.isAxiosError(e) ? e.response?.data?.error?.message || e.message : 'Failed to load';
        setError(String(msg));
      } finally {
        setLoading(false);
      }
    },
    [settings, isSecretConfigured, createdRange]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const applyFilters = () => {
    const p = new URLSearchParams();
    if (from) {
      p.set('from', from);
    }
    if (to) {
      p.set('to', to);
    }
    if (search) {
      p.set('search', search);
    }
    setSearchParams(p, { replace: true });
    void load(false);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return charges;
    }
    return charges.filter((ch) => {
      const desc = (ch.description || '').toLowerCase();
      const cust = formatCustomerDisplay(ch.customer).toLowerCase();
      const email = (ch.receipt_email || '').toLowerCase();
      return desc.includes(q) || cust.includes(q) || email.includes(q);
    });
  }, [charges, search]);

  const summary = useMemo(() => {
    let totalCents = 0;
    let paidCount = 0;
    let failedCount = 0;
    let currency = 'usd';
    for (const ch of filtered) {
      currency = ch.currency || currency;
      if (ch.paid) {
        totalCents += ch.amount;
        paidCount += 1;
      } else if (ch.status === 'failed') {
        failedCount += 1;
      }
    }
    return { totalCents, paidCount, failedCount, count: filtered.length, currency };
  }, [filtered]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <StripeHero
          compact
          title="Stripe · Transactions"
          subtitle="Card and wallet charges — filter by date and search description or customer."
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load(false)} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<PaidOutlinedIcon />}
            label="Paid volume"
            value={fmtStripeMoney(summary.totalCents, summary.currency)}
            sub={`${summary.paidCount} successful`}
            color="#635bff"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<ReceiptLongOutlinedIcon />}
            label="Charges shown"
            value={String(summary.count)}
            sub="In loaded page(s)"
            color="#0d9488"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<ErrorOutlineIcon />}
            label="Failed"
            value={String(summary.failedCount)}
            sub="In current filter"
            color="#ef4444"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StripeStatCard
            icon={<PaidOutlinedIcon />}
            label="Date range"
            value={`${from.slice(5)} → ${to.slice(5)}`}
            sub="Invoice/charge created"
            color="#7c3aed"
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
            <TextField
              label="From"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <TextField
              label="Search"
              size="small"
              placeholder="Description, customer, email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
            />
            <Button variant="contained" onClick={applyFilters}>
              Apply
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Payment</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((ch) => (
              <TableRow key={ch.id} hover>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography fontWeight={700}>{formatChargeDisplay(ch)}</Typography>
                </TableCell>
                <TableCell>{fmtStripeMoney(ch.amount, ch.currency)}</TableCell>
                <TableCell>
                  <StripeStatusChip label={ch.paid ? 'paid' : ch.status} color={ch.paid ? 'success' : 'default'} />
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography fontWeight={600}>{formatCustomerDisplay(ch.customer)}</Typography>
                  {ch.receipt_email && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {ch.receipt_email}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 240 }}>
                  <Typography variant="body2" noWrap title={ch.description || ''}>
                    {ch.description || '—'}
                  </Typography>
                  {ch.failure_message && (
                    <Typography variant="caption" color="error" display="block" noWrap>
                      {ch.failure_message}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtStripeUnix(ch.created)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="View in Stripe">
                    <Link href={`${dashBase}/charges/${ch.id}`} target="_blank" rel="noopener noreferrer">
                      <OpenInNewIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                    </Link>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" py={4} textAlign="center">
                    No charges in this window.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {hasMore && (
        <Button sx={{ mt: 2 }} disabled={loading} onClick={() => void load(true, cursor)}>
          {loading ? <CircularProgress size={22} /> : 'Load more'}
        </Button>
      )}
    </Box>
  );
};

export default StripeTransactionsPage;
